import { useEffect, useState } from "react";
import { IReactionDisposer, reaction } from "mobx";
import { electronModule } from "@/modules/electron";
import { useAppStore } from "@/store";

export const useSettingsNotificationPreferencesPageBehavior = () => {
  const { store } = useAppStore();
  const [meetingNotificationEnabled, setMeetingNotificationEnabled] = useState(false);
  const [teamsMeetingDetectionEnabled, setTeamsMeetingDetectionEnabled] = useState(false);
  const [calendarNotificationEnabled, setCalendarNotificationEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isDesktop = store.interface.isDesktop;

  useEffect(() => {
    const asyncLogic = async () => {
      const { enabled } = await electronModule.getMeetingNotificationEnabled();
      setMeetingNotificationEnabled(enabled);
      const { enabled: calendarNotificationEnabled } =
        await electronModule.getCalendarNotificationEnabled();
      setCalendarNotificationEnabled(calendarNotificationEnabled);
      const { enabled: teamsMeetingDetectionEnabled } =
        await electronModule.getTeamsMeetingDetectionEnabled();
      setTeamsMeetingDetectionEnabled(teamsMeetingDetectionEnabled);
      store.systemPermissions.setTeamsMeetingDetectionEnabled({
        enabled: teamsMeetingDetectionEnabled,
      });
    };
    asyncLogic();
  }, [store.systemPermissions]);

  const waitForMeetingOrVoicePermissionGrant = (): Promise<boolean> => {
    const hadVoicePermissions = store.systemPermissions.hasRequiredVoicePermissions;
    const hadMeetingPermissions = store.systemPermissions.hasRequiredMeetingPermissions;
    if (hadMeetingPermissions && hadVoicePermissions) {
      return Promise.resolve(true);
    }

    return new Promise(resolve => {
      let dispose: IReactionDisposer | null = null;
      let settled = false;

      const finish = (result: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        dispose?.();
        resolve(result);
      };

      dispose = reaction(
        () => ({
          hasMeetingPermissions: store.systemPermissions.hasRequiredMeetingPermissions,
          hasVoicePermissions: store.systemPermissions.hasRequiredVoicePermissions,
          isModalOpen:
            store.systemPermissions.ui.isMeetingModalOpen ||
            store.systemPermissions.ui.isVoiceModalOpen,
        }),
        ({ hasMeetingPermissions, hasVoicePermissions, isModalOpen }) => {
          if (!hadMeetingPermissions && hasMeetingPermissions) {
            finish(true);
            return;
          }

          if (!hadVoicePermissions && hasVoicePermissions) {
            finish(true);
            return;
          }

          if (!isModalOpen) {
            finish(false);
          }
        }
      );
    });
  };

  const waitForAdvancedMeetingPermissionGrant = (): Promise<boolean> => {
    if (store.systemPermissions.hasRequiredAdvancedMeetingPermissions) {
      return Promise.resolve(true);
    }

    return new Promise(resolve => {
      let dispose: IReactionDisposer | null = null;
      let settled = false;

      const finish = (result: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        dispose?.();
        resolve(result);
      };

      dispose = reaction(
        () => ({
          hasPermissions: store.systemPermissions.hasRequiredAdvancedMeetingPermissions,
          isModalOpen: store.systemPermissions.ui.isAdvancedMeetingModalOpen,
        }),
        ({ hasPermissions, isModalOpen }) => {
          if (hasPermissions) {
            finish(true);
            return;
          }

          if (!isModalOpen) {
            finish(false);
          }
        }
      );
    });
  };

  const toggleMeetingNotificationPreference = async () => {
    if (!isDesktop || isSaving) return;

    setIsSaving(true);
    try {
      const enabling = !meetingNotificationEnabled;

      if (enabling) {
        const permissionMap = await electronModule.recall.getPermissionStatus();
        store.systemPermissions.setPermissionMap({ permissionMap });

        if (!store.systemPermissions.hasRequiredMeetingPermissions) {
          store.systemPermissions.ui.openMeetingPermissionsModal();
          const granted = await waitForMeetingOrVoicePermissionGrant();
          if (!granted) {
            return;
          }
        }
        if (!store.systemPermissions.hasRequiredVoicePermissions) {
          store.systemPermissions.ui.openVoicePermissionsModal();
          const granted = await waitForMeetingOrVoicePermissionGrant();
          if (!granted) {
            return;
          }
        }
      }

      setMeetingNotificationEnabled(enabling);
      await electronModule.setMeetingNotificationEnabled({ enabled: enabling });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCalendarNotificationPreference = async () => {
    if (!isDesktop || isSaving) return;

    setIsSaving(true);
    try {
      const enabling = !calendarNotificationEnabled;

      setCalendarNotificationEnabled(enabling);
      await electronModule.setCalendarNotificationEnabled({ enabled: enabling });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTeamsMeetingDetection = async () => {
    if (!isDesktop || isSaving) return;

    setIsSaving(true);
    const enabling = !teamsMeetingDetectionEnabled;
    store.systemPermissions.setTeamsMeetingDetectionEnabled({
      enabled: enabling,
    });
    try {
      if (enabling) {
        const permissionMap = await electronModule.recall.getPermissionStatus();
        store.systemPermissions.setPermissionMap({ permissionMap });

        if (!store.systemPermissions.hasRequiredAdvancedMeetingPermissions) {
          store.systemPermissions.ui.openAdvancedMeetingPermissionsModal();
          const granted = await waitForAdvancedMeetingPermissionGrant();
          if (!granted) {
            store.systemPermissions.setTeamsMeetingDetectionEnabled({
              enabled: enabling,
            });
            return;
          }
        }
      }

      setTeamsMeetingDetectionEnabled(enabling);
      await electronModule.setTeamsMeetingDetectionEnabled({ enabled: enabling });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    meetingNotificationEnabled,
    teamsMeetingDetectionEnabled,
    calendarNotificationEnabled,
    isSaving,
    toggleMeetingNotificationPreference,
    toggleCalendarNotificationPreference,
    toggleTeamsMeetingDetection,
  };
};
